/** Drivers ro'yxati (eskiz 8). Filtrlar URL'da turadi: yuqori paneldagi qidiruv ?q= shu sahifaga keladi. */
import { rememberPageSize, urlPageSize } from "@/lib/pageSize";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Plus, TriangleAlert } from "lucide-react";
import { cn, dayjs, formatWhen } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import { isManager } from "@/features/auth/types";
import { useDriverCounts, useDrivers, useUserOptions, type DriverQuery } from "@/features/drivers/api";
import { DRIVER_PROBLEMS, DRIVER_TYPES, LEGACY_DRIVER_TYPES, LOST_STATUSES, PIPELINE_ORDER, SOURCE_LABEL, LEGAL_STATUS_LABEL, US_STATES, type DriverListItem, type DriverStatus, type Source, type LegalStatus } from "@/features/drivers/types";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/ui/badge";
import { FilterSelect, Input } from "@/components/ui/field";
import { EmptyState, ErrorState, PageHeader, SimilarNote, Spinner } from "@/components/ui/misc";
import { FilterChips, Pagination, Table, Td, Th, Tr } from "@/components/ui/table";
import { ContextMenu } from "@/components/ui/context-menu";
import { useDriverActions } from "./useDriverActions";
import { PersonLink } from "@/components/PersonLink";
import { ScopePicker } from "@/components/ScopePicker";
import { SectionSwitch } from "@/components/SectionSwitch";
import { canPickBranch, scopedBranch, useBranchFilter } from "@/app/branchFilter";
import { readStageMonth, recentMonths, STAGE_EVENTS, STAGE_MONTH_KEYS, stageMonthPatch } from "@/features/drivers/stageMonth";

const DATE_ADDED = [
  ["today", "today"],
  ["7d", "d7"],
  ["30d", "d30"],
  ["90d", "d90"],
  ["180d", "d180"],
  ["365d", "y1"],
  ["older_90d", "older90d"],
  ["older_180d", "older180d"],
  ["older_1y", "older1y"],
  ["custom", "custom"],
] as const;


export function DriversPage() {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const me = useMe().data;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const driverActions = useDriverActions();
  const { branchId, setBranchId } = useBranchFilter();

  const manager = me ? isManager(me.role) : false;
  const lead = manager;
  const statuses = params.getAll("status") as DriverStatus[];
  const get = (k: string) => params.get(k) ?? "";
  const page = Math.max(1, Number(get("page") || 1));
  const pageSize = urlPageSize(get("page_size"), "drivers");
  const ordering = get("ordering") || "-created_at";

  const baseQuery: DriverQuery = {
    q: get("q"),
    source: get("source"),
    legal_status: get("legal_status"),
    state: get("state"),
    cdl_class: get("cdl_class"),
    driver_type: get("driver_type"),
    freight_type: get("freight_type"),
    date_added: get("date_added"),
    date_from: get("date_from"),
    date_to: get("date_to"),
    // "Oyda bosqich" (features/drivers/stageMonth.ts): bosh sahifa / hisobotdan ham keladi, qo'lda ham tanlanadi
    ...Object.fromEntries(STAGE_MONTH_KEYS.map((k) => [k, get(k)])),
    problem: get("problem"),
    owner: get("owner"),
    branch: me ? scopedBranch(me, branchId) : undefined,
  };
  const query: DriverQuery = { ...baseQuery, status: statuses, page, page_size: pageSize, ordering };

  const stage = readStageMonth(get);
  const months = recentMonths(24, stage?.month);

  // Hisobotdan filial bilan kelinsa (?branch=) -- sahifa tepasidagi filial tanlovi shunga o'tadi, ro'yxat va sonlar o'sha filial bo'yicha
  const urlBranch = get("branch");
  useEffect(() => {
    if (!urlBranch || !me) return;
    if (canPickBranch(me)) setBranchId(Number(urlBranch) || null);
    const next = new URLSearchParams(params);
    next.delete("branch");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlBranch, me]);

  const drivers = useDrivers(query);
  const counts = useDriverCounts(baseQuery);
  const owners = useUserOptions({ role: "recruiter" }, lead);

  function update(patch: Record<string, string | string[] | null>, keepPage = false) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      next.delete(k);
      if (Array.isArray(v)) v.forEach((x) => next.append(k, x));
      else if (v) next.set(k, v);
    }
    if (!keepPage) next.delete("page");
    setParams(next, { replace: true });
  }

  const hasFilters = ["q", "source", "legal_status", "driver_type", "freight_type", "state", "cdl_class", "date_added", "date_from", "date_to", "owner", "problem", ...STAGE_MONTH_KEYS].some((k) => get(k)) || statuses.length > 0;
  const dateAddedValue = get("date_from") || get("date_to") || get("date_added") === "custom" ? "custom" : get("date_added");
  const c = counts.data ?? {};
  const chipValue = statuses.length === 0 ? "all" : statuses.length === 1 ? statuses[0] : LOST_STATUSES.every((s) => statuses.includes(s)) ? "lost" : "all";
  const chips = [
    { key: "all", label: t("list.chips.all"), count: c.all ?? 0 },
    ...PIPELINE_ORDER.map((s) => ({ key: s, label: tc(`status.${s}`), count: c[s] ?? 0 })),
    { key: "lost", label: t("list.chips.lost"), count: c.lost ?? 0 },
  ];

  function sortBy(field: string) {
    update({ ordering: ordering === field ? `-${field}` : field }, true);
  }

  const scopeLabel = !me ? "" : t(`list.scope.${me.role === "carrier" ? "owner" : me.role}`);
  const rows = drivers.data?.results ?? [];
  const allOnPage = rows.length > 0 && rows.every((d) => selected.has(d.id));

  return (
    <div>
      <PageHeader
        title={t("list.title")}
        subtitle={counts.data ? `${scopeLabel} · ${c.all ?? 0}` : scopeLabel}
        actions={
          <>
            {me && <ScopePicker me={me} />}
            <SectionSwitch ariaLabel={tc("nav.drivers")} items={[{ to: "/drivers", label: tc("nav.list") }, { to: "/pipeline", label: tc("nav.board") }]} />
            <Link to="/drivers/new">
              <Button variant="primary">
                <Plus size={14} strokeWidth={2.25} /> {t("list.newDriver")}
              </Button>
            </Link>
          </>
        }
      >
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <FilterSelect value={get("problem")} onChange={(e) => update({ problem: e.target.value })} className="w-44" aria-label={t("list.filters.problem")}>
            <option value="">{t("list.filters.problem")}</option>
            <option value="any">{t("list.problem.any")}</option>
            {DRIVER_PROBLEMS.map((p) => (
              <option key={p} value={p}>
                {t(`list.problem.${p}`)}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect value={get("source")} onChange={(e) => update({ source: e.target.value })} className="w-36" aria-label={t("list.filters.source")}>
            <option value="">{t("list.filters.source")}</option>
            {(Object.keys(SOURCE_LABEL) as Source[]).map((s) => (
              <option key={s} value={s}>
                {tc(`source.${s}`)}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect value={get("legal_status")} onChange={(e) => update({ legal_status: e.target.value })} className="w-40" aria-label={t("list.filters.legalStatus")}>
            <option value="">{t("list.filters.legalStatus")}</option>
            {(Object.keys(LEGAL_STATUS_LABEL) as LegalStatus[]).map((ls) => (
              <option key={ls} value={ls}>
                {tc(`legalStatus.${ls}`)}
              </option>
            ))}
          </FilterSelect>
          {/* Mijoz ishining asosiy o'lchovlari: driver turi va yuk turi */}
          <FilterSelect value={get("driver_type")} onChange={(e) => update({ driver_type: e.target.value })} className="w-40" aria-label={t("list.filters.driverType")}>
            <option value="">{t("list.filters.driverType")}</option>
            {[...DRIVER_TYPES, ...LEGACY_DRIVER_TYPES].map((x) => (
              <option key={x} value={x}>
                {tc(`driverType.${x}`)}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect value={get("freight_type")} onChange={(e) => update({ freight_type: e.target.value })} className="w-36" aria-label={t("list.filters.freight")}>
            <option value="">{t("list.filters.freight")}</option>
            {(["amazon", "street", "both"] as const).map((x) => (
              <option key={x} value={x}>
                {tc(`freight.${x}`)}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect value={get("state")} onChange={(e) => update({ state: e.target.value })} className="w-28" aria-label={t("list.filters.state")}>
            <option value="">{t("list.filters.state")}</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect value={get("cdl_class")} onChange={(e) => update({ cdl_class: e.target.value })} className="w-32" aria-label={t("list.filters.cdlClass")}>
            <option value="">{t("list.filters.cdlClass")}</option>
            {["A", "B", "C"].map((s) => (
              <option key={s} value={s}>
                {t("list.filters.classN", { cls: s })}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={dateAddedValue}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "custom") {
                update({ date_added: "custom" });
              } else {
                update({ date_added: v || null, date_from: null, date_to: null });
              }
            }}
            className="w-44"
            aria-label={t("list.filters.dateAdded")}
          >
            <option value="">{t("list.filters.dateAdded")}</option>
            {DATE_ADDED.map(([k, label]) => (
              <option key={k} value={k}>
                {t(`list.dateAdded.${label}`)}
              </option>
            ))}
          </FilterSelect>
          {dateAddedValue === "custom" && (
            <span className="flex items-center gap-1">
              <Input
                type="date"
                value={get("date_from")}
                max={get("date_to") || undefined}
                onChange={(e) => update({ date_from: e.target.value || null, date_added: "custom" })}
                className="w-[145px]"
                aria-label={t("list.dateAdded.from")}
                title={t("list.dateAdded.from")}
              />
              <span className="text-muted">–</span>
              <Input
                type="date"
                value={get("date_to")}
                min={get("date_from") || undefined}
                onChange={(e) => update({ date_to: e.target.value || null, date_added: "custom" })}
                className="w-[145px]"
                aria-label={t("list.dateAdded.to")}
                title={t("list.dateAdded.to")}
              />
            </span>
          )}
          {lead && (
            <FilterSelect value={get("owner")} onChange={(e) => update({ owner: e.target.value })} className="w-44" aria-label={t("list.filters.recruiter")}>
              <option value="">{t("list.filters.allRecruiters")}</option>
              {(owners.data?.results ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </FilterSelect>
          )}
          {/* Oyda bosqich: bosqich + oy (bosh sahifa va hisobot ko'rsatkichlari shu filtrni qo'yadi) */}
          <FilterSelect
            value={stage?.event ?? ""}
            onChange={(e) => update(stageMonthPatch(e.target.value as typeof STAGE_EVENTS[number] | "", stage?.month ?? months[0]))}
            className="w-44"
            aria-label={t("list.filters.stageMonth")}
          >
            <option value="">{t("list.filters.stageMonth")}</option>
            {STAGE_EVENTS.map((ev) => (
              <option key={ev} value={ev}>
                {ev === "new" ? t("list.filters.stageAdded") : ev === "lost" ? t("list.chips.lost") : tc(`status.${ev}`)}
              </option>
            ))}
          </FilterSelect>
          {stage && (
            <FilterSelect value={stage.month} onChange={(e) => update(stageMonthPatch(stage.event, e.target.value))} className="w-36" aria-label={t("list.filters.month")}>
              {months.map((m) => (
                <option key={m} value={m}>
                  {dayjs(`${m}-01`).format("MMM YYYY")}
                </option>
              ))}
            </FilterSelect>
          )}
          {hasFilters && (
            <Button variant="link" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              {tc("actions.clearFilters")}
            </Button>
          )}
          {get("q") && (
            <span className="text-[12px] text-muted">
              {t("list.search")} <span className="font-semibold text-ink">{get("q")}</span>
            </span>
          )}
        </div>
        <div className="mt-3">
          <FilterChips
            items={chips}
            value={chipValue}
            onChange={(k) => update({ status: k === "all" ? [] : k === "lost" ? LOST_STATUSES : [k] })}
          />
        </div>
      </PageHeader>

      {drivers.isPending ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner />
        </div>
      ) : drivers.isError ? (
        <ErrorState message={drivers.error.message} onRetry={() => drivers.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={hasFilters ? t("list.empty.filteredTitle") : t("list.empty.title")}
          description={hasFilters ? t("list.empty.filteredHint") : t("list.empty.hint")}
          action={
            hasFilters ? (
              <Button onClick={() => setParams(new URLSearchParams(), { replace: true })}>{tc("actions.clearFilters")}</Button>
            ) : (
              <Link to="/drivers/new">
                <Button variant="primary">
                  <Plus size={14} /> {t("list.newDriver")}
                </Button>
              </Link>
            )
          }
        />
      ) : (
        <div className={cn(drivers.isFetching && "opacity-70 transition-opacity")}>
          <SimilarNote show={drivers.data?.fuzzy} />
          <Table>
            <thead>
              <tr>
                <Th className="w-9">
                  <input
                    type="checkbox"
                    aria-label={t("list.selectAll")}
                    className="h-4 w-4 accent-primary"
                    checked={allOnPage}
                    onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((d) => d.id)) : new Set())}
                  />
                </Th>
                <SortTh field="last_name" ordering={ordering} onSort={sortBy}>
                  {t("list.cols.name")}
                </SortTh>
                <Th>{t("list.cols.phone")}</Th>
                <SortTh field="state" ordering={ordering} onSort={sortBy}>
                  {t("list.cols.state")}
                </SortTh>
                <Th>{t("list.cols.cdl")}</Th>
                <SortTh field="status_changed_at" ordering={ordering} onSort={sortBy}>
                  {t("list.cols.status")}
                </SortTh>
                <Th>{t("list.cols.carrier")}</Th>
                {lead && <Th>{t("list.cols.owner")}</Th>}
                <SortTh field="last_contact_at" ordering={ordering} onSort={sortBy}>
                  {t("list.cols.lastContact")}
                </SortTh>
                <Th>{t("list.cols.nextAction")}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <ContextMenu key={d.id} items={() => driverActions.items(d)}>
                  <Tr clickable onClick={() => navigate(`/drivers/${d.id}`)}>
                    <Td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={t("list.selectOne", { name: d.full_name })}
                        className="h-4 w-4 accent-primary"
                        checked={selected.has(d.id)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(d.id);
                          else next.delete(d.id);
                          setSelected(next);
                        }}
                      />
                    </Td>
                    <Td>
                      <Link to={`/drivers/${d.id}`} className="font-semibold hover:underline" onClick={(e) => e.stopPropagation()}>
                        {d.full_name}
                      </Link>
                      {!!d.problems?.length && (
                        <span className="ml-1.5 inline-flex align-middle text-warning" title={d.problems.map((p) => t(`list.problem.${p}`)).join(" · ")} aria-label={d.problems.map((p) => t(`list.problem.${p}`)).join(", ")}>
                          <TriangleAlert size={13} strokeWidth={1.75} aria-hidden />
                        </span>
                      )}
                      {(d.driver_type || d.freight_type) && (
                        <div className="text-[11px] text-muted">{[d.driver_type && tc(`driverType.${d.driver_type}`), d.freight_type && tc(`freight.${d.freight_type}`)].filter(Boolean).join(" · ")}</div>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap">{d.phone_display || <span className="text-muted">—</span>}</Td>
                    <Td>{d.state || "—"}</Td>
                    <Td className="whitespace-nowrap">{[d.cdl_class, d.experience_label].filter(Boolean).join(" · ") || "—"}</Td>
                    <Td>
                      <StatusPill status={d.status} />
                    </Td>
                    <Td className="max-w-[180px] truncate">
                      {d.current_job ? (
                        <Link to={`/carriers/${d.current_job.carrier_id}`} className="hover:underline" onClick={(e) => e.stopPropagation()} title={d.current_job.title}>
                          {d.current_job.carrier_name}
                        </Link>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </Td>
                    {lead && <Td className="whitespace-nowrap">{d.owner ? <PersonLink id={d.owner.id} name={d.owner.display_name} /> : <span className="text-muted">{t("list.pool")}</span>}</Td>}
                    <Td className={cn("whitespace-nowrap", !d.last_contact_at && "text-muted")}>{formatWhen(d.last_contact_at)}</Td>
                    <Td>
                      <NextAction d={d} />
                    </Td>
                  </Tr>
                </ContextMenu>
              ))}
            </tbody>
          </Table>
          <Pagination
            page={drivers.data.page}
            pages={drivers.data.pages}
            count={drivers.data.count}
            pageSize={drivers.data.page_size}
            onPage={(p) => update({ page: String(p) }, true)}
            onPageSize={(s) => {
              rememberPageSize("drivers", s);
              update({ page_size: String(s) });
            }}
            label={t("list.pagLabel")}
          />
        </div>
      )}
      {driverActions.dialogs}
    </div>
  );
}

function SortTh({ field, ordering, onSort, children }: { field: string; ordering: string; onSort: (f: string) => void; children: ReactNode }) {
  const active = ordering === field || ordering === `-${field}`;
  const desc = ordering === `-${field}`;
  return (
    <Th>
      <button type="button" onClick={() => onSort(field)} className={cn("inline-flex items-center gap-1 hover:text-ink", active && "text-ink")}>
        {children}
        {active && (desc ? <ArrowDown size={11} /> : <ArrowUp size={11} />)}
      </button>
    </Th>
  );
}

/** Keyingi ish: eng yaqin eslatma, kechikkani qizil; yo'qotilganlar uchun sabab */
function NextAction({ d }: { d: DriverListItem }) {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  if (LOST_STATUSES.includes(d.status)) {
    return <span className="text-muted">{d.status_reason ? t("list.next.reason", { reason: d.status_reason }) : "—"}</span>;
  }
  const r = d.next_reminder;
  if (!r) {
    if (d.status === "new" && !d.last_contact_at) return <span>{t("list.next.firstCall")}</span>;
    if (d.status === "submitted") return <span className="text-muted">{t("list.next.waitingCarrier")}</span>;
    return <span className="text-muted">—</span>;
  }
  const label = tc(`reminder.${r.kind}`);
  if (r.overdue) {
    const days = dayjs().startOf("day").diff(dayjs(r.due_at).startOf("day"), "day");
    return (
      <span className="font-semibold text-danger">
        {days <= 0 ? t("list.next.overdueToday", { label }) : t("list.next.overdueDays", { label, count: days })}
      </span>
    );
  }
  return (
    <span>
      {label} · {dayjs(r.due_at).format("MMM D, HH:mm")}
    </span>
  );
}
